import React, { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import SvgMinus from '@/src/assets/svgs/collection/minus.svg';
import SvgPlus from '@/src/assets/svgs/collection/plus.svg';
import ItemInfos from '@/src/interfaces/catalog/item-infos';
import { SelectAnswerProps, useCurrentProduct } from '@/src/context/product/current-product-context';
import { useMerchant } from '@/src/context/merchant/merchant-context';
import BottomSheet from '@/src/components/helpers/overlays/bottom-sheet/bottom-sheet';
import ModalAddCustom from '@/src/components/product/modal-add-custom/modal-add-custom';
import { useLongPress } from '@/src/hooks/useLongPress';

interface Props {
    itemOption: ItemInfos.ItemOption;
    itemGroup: ItemInfos.ItemGroup;
}

export type AnswerType = 'checkbox' | 'radio' | 'add';

export default function Answer(props: Props) {
    const { handleSelectedAnswer, answersFromCustomer } = useCurrentProduct();
    const { localMode, merchantSummary } = useMerchant()
    const [quantity, setQuantity] = useState<number>(0);
    const [answerType, setAnswerType] = useState<AnswerType>('radio');
    const [isCombo, setIsCombo] = useState<boolean>(false);
    const [disableOption, setDisableOption] = useState<boolean>(false);

    const [bottomSheet, setBottomSheet] = useState(false)

    const increaseRef = useRef<HTMLDivElement>(null)
    const decreaseRef = useRef<HTMLDivElement>(null)

    const longPress = useLongPress({
        onLongPress: () => setBottomSheet(true),
        delay: 500
    })

    const renderItemPrice = (): JSX.Element => {
        const isObservationAnswer = !!props.itemOption.answerForCustomer && !!props.itemOption.answerForMerchant;

        if (isObservationAnswer || isCombo || !merchantSummary?.showProductPrice) return <></>;

        let price = 0;
        let currentQuantity = quantity === 0 ? 1 : quantity;

        if (props.itemOption.promoPrice === undefined) price = props.itemOption.itemChild?.salePrice ?? 0;

        if (props.itemOption.promoPrice && props.itemOption.promoPrice > 0) price = props.itemOption.promoPrice;

        if (price > 0)
            return <span className={styles.price}>+{(price * currentQuantity).toLocalePrice()}</span>;

        return <></>
    };

    const renderTitle = (): JSX.Element => {
        let title: string = '';

        if (isCombo && (props.itemOption.itemChild?.sizes.length ?? 0) > 0)
            title = `${props.itemOption.itemChild?.sizes[0].sizeType.name} ${props.itemOption.itemChild?.name}`;
        else if (props.itemOption.itemId)
            title = `${props.itemOption.itemChild?.name}`;
        else
            title = `${props.itemOption.answerForCustomer}`;

        const quantityItem = (answerType !== 'add' && props.itemOption.quantity > 1) ? `${props.itemOption.quantity}x ` : '';

        return (
            <p
                className={`font-xmd ${styles.answerTitle} ${isSelected(props.itemOption.id, props.itemGroup.id) ? styles.titleAnswered : ''
                    }`}
            >
                {quantityItem}{title}
            </p>
        );
    };

    const handleUpdateQuantity = (newQuantity: number) => {
        setQuantity(newQuantity);

        const answerProps: SelectAnswerProps = {
            itemOption: props.itemOption,
            itemGroup: props.itemGroup,
            isCombo: false,
            mustUpdatePrice: true,
            quantity: newQuantity,
            answerType: answerType
        }

        handleSelectedAnswer(answerProps);
    };

    const handleClick = () => {
        let quantityItem = props.itemOption.quantity > 1 ? props.itemOption.quantity : quantity

        const answerProps: SelectAnswerProps = {
            itemOption: props.itemOption,
            itemGroup: props.itemGroup,
            isCombo: isCombo,
            mustUpdatePrice: false,
            quantity: quantityItem || 1,
            answerType: answerType
        }

        if (isSelected(props.itemOption.id, props.itemGroup.id) && answerType != 'radio') {
            // Deseleciona o item se já estiver selecionado
            answerProps.quantity = 0
        }

        // Seleciona o item se o máximo de respostas ainda não foi atingido
        handleSelectedAnswer(answerProps);
    };

    const isSelected = (answerId: string, questionId: string): boolean => {
        if (!answersFromCustomer) return false;

        const currentAnswer = answersFromCustomer.find((c) => c.id === questionId);

        return !!currentAnswer?.itemOptionsGroup?.find((c) => c.id === answerId);
    };

    const isFullyAnswered = (canAddMore: boolean = false): boolean => {
        if (answerType === 'radio')
            return false

        const selectedCount = answersFromCustomer?.find((c) => c.id === props.itemGroup.id)?.itemOptionsGroup?.length || 0;

        if (!props.itemGroup.isRequired && selectedCount < props.itemGroup.maxQtyAnswer) return false

        const sum = sumOfQtyAnswered();
        const validateSumQty = sum >= props.itemGroup.maxQtyItems;

        if (canAddMore)
            return selectedCount <= props.itemGroup.maxQtyAnswer && validateSumQty;
        else
            return selectedCount >= props.itemGroup.maxQtyAnswer || validateSumQty;
    };

    const sumOfQtyAnswered = () => {
        const currentAnswers = answersFromCustomer.filter((c) => c.id === props.itemGroup.id);
        const sumOfQtyAnswered = currentAnswers.reduce((a, b) => {
            const qty = b.itemOptionsGroup.reduce((x, y) => x + y.quantity, 0)
            return a + qty;
        }, 0);

        return sumOfQtyAnswered;
    }

    const renderAnswerType = () => {
        const unlimited = props.itemOption.maxQty === 0 && props.itemOption.minQty === 0;

        if (answerType === 'checkbox')
            return (
                <div
                    className={`${isSelected(props.itemOption.id, props.itemGroup.id)
                        ? styles.checkedSquareAnswer
                        : styles.checkSquareAnswer
                        }`}
                />
            );

        if (answerType === 'radio')
            return (
                <div
                    className={
                        isSelected(props.itemOption.id, props.itemGroup.id) ? styles.checkedAnswer : styles.checkAnswer
                    }
                />
            );

        return (
            <div className={`${styles.renderQuantityOptions} ${quantity > 0 ? styles.addBorder : ''}`}>
                {
                    quantity > 0 &&
                    <>
                        <div
                            ref={decreaseRef}
                            className={styles.minus}
                            onClick={() => handleUpdateQuantity(quantity - 1)}
                        >
                            <SvgMinus className={styles.svgMinus} />
                        </div>
                        <span className={styles.quantity}>
                            {quantity}
                        </span>
                    </>
                }
                <div
                    ref={increaseRef}
                    className={`${styles.plus} ${(props.itemOption.maxQty === quantity && !unlimited)
                        || isFullyAnswered(true) ? styles.disablePlus : ''}`}
                    onClick={() => handleUpdateQuantity(quantity + 1)}
                >
                    <SvgPlus className={styles.svgPlus} />
                </div>
            </div>
        );
    };

    useEffect(() => {
        const isObservation = !props.itemOption.itemChild || props.itemGroup.itemOptionsGroup.every(i => i.maxQty === 1)

        const canJustOneAnswer = props.itemGroup.maxQtyAnswer === 1 && props.itemGroup.isRequired
        const canJustOneObservation = props.itemGroup.maxQtyItems === 1 || isObservation
        const _isCombo = props.itemGroup.groupingType === 'Combo'

        if ((canJustOneAnswer && canJustOneObservation) || _isCombo)
            setAnswerType('radio');
        else if (canJustOneObservation)
            setAnswerType('checkbox');
        else
            setAnswerType('add');

        setIsCombo(_isCombo);
    }, [props.itemGroup, props.itemOption]);

    useEffect(() => {
        setDisableOption(isFullyAnswered() && !isSelected(props.itemOption.id, props.itemGroup.id));
    }, [answersFromCustomer, props.itemGroup, props.itemOption]);

    const handleCustomValueConfirm = (customValue: number) => {
        handleUpdateQuantity(customValue)
        setBottomSheet(false)
    }

    useEffect(() => {
        if (answerType === 'add') {
            const cleanupIncrease = longPress.attachListeners(increaseRef.current)
            const cleanupDecrease = longPress.attachListeners(decreaseRef.current)

            return () => {
                cleanupIncrease?.()
                cleanupDecrease?.()
            }
        }
    }, [answerType, quantity])

    return (
        <>
            {bottomSheet && (
                <BottomSheet isOpen={bottomSheet} onClose={() => setBottomSheet(false)}>
                    <ModalAddCustom
                        onConfirm={handleCustomValueConfirm}
                        initialValue={quantity}
                        wizard
                        maxValue={props.itemOption.maxQty}
                        sumOfQtyAnswered={sumOfQtyAnswered()}
                    />
                </BottomSheet>
            )}
            <div
                id={props.itemOption.id}
                className={`${styles.answer} ${disableOption ? styles.disable : ''}`}
                onClick={() => answerType !== 'add' && handleClick()}
            >
                {renderTitle()}
                {renderItemPrice()}
                {!localMode && renderAnswerType()}
            </div>
        </>
    );
}
